import React from 'react';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { contactInfo } from '../mockData';
import { Card } from '../components/ui/card';

const Contact = () => {
  const { t } = useLanguage();

  const contactDetails = [
    {
      icon: Phone,
      label: t.contact.info.phone,
      value: contactInfo.phone,
      href: `tel:${contactInfo.phone}`,
    },
    {
      icon: Mail,
      label: t.contact.info.email,
      value: contactInfo.email,
      href: `mailto:${contactInfo.email}`,
    },
    {
      icon: MapPin,
      label: t.contact.info.address,
      value: contactInfo.address,
      href: null,
    },
    {
      icon: Clock,
      label: t.contact.info.hours,
      value: contactInfo.hours,
      href: null,
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzcwNDAzNzgwfDA&ixlib=rb-4.1.0&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 tracking-tight">
            {t.contact.title}
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 font-light">
            {t.contact.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactDetails.map((detail, index) => {
              const Icon = detail.icon;
              const content = (
                <Card
                  className="p-8 border-gray-200 hover:shadow-xl transition-all duration-300 group h-full"
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s backwards`,
                  }}
                >
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {detail.label}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {detail.value}
                    </p>
                  </div>
                </Card>
              );

              if (detail.href) {
                return (
                  <a
                    key={index}
                    href={detail.href}
                    className="block hover:scale-105 transition-transform duration-300"
                  >
                    {content}
                  </a>
                );
              }

              return <div key={index}>{content}</div>;
            })}
          </div>

          {/* Social Media Section */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              {t.contact.social}
            </h2>
            <div className="flex items-center justify-center space-x-6">
              <a
                href={contactInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center hover:bg-black transition-all duration-300 hover:scale-110 group"
              >
                <Facebook className="h-8 w-8 text-white" />
              </a>
              <a
                href={contactInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center hover:bg-black transition-all duration-300 hover:scale-110 group"
              >
                <Instagram className="h-8 w-8 text-white" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder / Additional Info Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="relative h-96 overflow-hidden rounded-lg shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1610526662524-bd113e746fdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxyZXNpZGVudGlhbCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzA0MDM3NzF8MA&ixlib=rb-4.1.0&q=85"
                alt="Office location"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col justify-center space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                {t.contact.cta}
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                {t.about.description}
              </p>
              <div className="flex flex-col space-y-4">
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="inline-flex items-center space-x-3 text-gray-700 hover:text-black transition-colors text-lg group"
                >
                  <Phone className="h-5 w-5 group-hover:scale-110 transition-transform" />
                  <span>{contactInfo.phone}</span>
                </a>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="inline-flex items-center space-x-3 text-gray-700 hover:text-black transition-colors text-lg group"
                >
                  <Mail className="h-5 w-5 group-hover:scale-110 transition-transform" />
                  <span>{contactInfo.email}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
